import { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc,
  deleteDoc,
  addDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { DeliveryNote, CreateDeliveryNoteData, DeliveryNoteStatus } from '../types/deliveryNote';
import toast from 'react-hot-toast';

export const useDeliveryNotes = () => {
  const [deliveryNotes, setDeliveryNotes] = useState<DeliveryNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    const deliveryNotesRef = collection(db, 'delivery_notes');
    const q = query(
      deliveryNotesRef,
      where('organizationId', '==', user.organizationId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const deliveryNotesData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
            updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
          } as DeliveryNote;
        });
        setDeliveryNotes(deliveryNotesData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching delivery notes:', err);
        setError('Hiba a szállítólevelek betöltésekor');
        setLoading(false);
        toast.error('Hiba a szállítólevelek betöltésekor');
      }
    );

    return () => unsubscribe();
  }, [user?.organizationId]);

  const createDeliveryNote = async (deliveryNoteData: CreateDeliveryNoteData): Promise<DeliveryNote | null> => {
    if (!user?.organizationId) {
      toast.error('Nincs szervezet kiválasztva');
      return null;
    }

    try {
      const now = new Date();
      const newDeliveryNote = {
        ...deliveryNoteData,
        organizationId: user.organizationId,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
        createdBy: user.uid
      };

      const docRef = await addDoc(collection(db, 'delivery_notes'), newDeliveryNote);
      
      const createdDeliveryNote: DeliveryNote = {
        ...deliveryNoteData,
        id: docRef.id,
        organizationId: user.organizationId,
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
        createdBy: user.uid
      };

      toast.success('Szállítólevél sikeresen létrehozva');
      return createdDeliveryNote;
    } catch (error) {
      console.error('Error creating delivery note:', error);
      toast.error('Hiba a szállítólevél létrehozásakor');
      return null;
    }
  };

  const updateDeliveryNote = async (deliveryNoteId: string, updates: Partial<DeliveryNote>): Promise<boolean> => {
    try {
      const deliveryNoteRef = doc(db, 'delivery_notes', deliveryNoteId);
      await updateDoc(deliveryNoteRef, {
        ...updates,
        updatedAt: Timestamp.fromDate(new Date())
      });
      
      toast.success('Szállítólevél sikeresen frissítve');
      return true;
    } catch (error) {
      console.error('Error updating delivery note:', error);
      toast.error('Hiba a szállítólevél frissítésekor');
      return false;
    }
  };

  const updateDeliveryNoteStatus = async (deliveryNoteId: string, status: DeliveryNoteStatus): Promise<boolean> => {
    return updateDeliveryNote(deliveryNoteId, { status });
  };

  const deleteDeliveryNote = async (deliveryNoteId: string): Promise<boolean> => {
    try {
      await deleteDoc(doc(db, 'delivery_notes', deliveryNoteId));
      toast.success('Szállítólevél sikeresen törölve');
      return true;
    } catch (error) {
      console.error('Error deleting delivery note:', error);
      toast.error('Hiba a szállítólevél törlésekor');
      return false;
    }
  };

  return {
    deliveryNotes,
    loading,
    error,
    createDeliveryNote,
    updateDeliveryNote,
    updateDeliveryNoteStatus,
    deleteDeliveryNote
  };
}; 